import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useWorkspaces, type Workspace } from "@/store/workspaces";

/**
 * Tiny menu attached to each workspace card. Rename + delete.
 *
 * The button stops event propagation so clicking it doesn't navigate into
 * the workspace (the card is a Link wrapper).
 */
export function WorkspaceCardMenu({ workspace }: { workspace: Workspace }) {
  const { renameWorkspace, deleteWorkspace } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"menu" | "rename" | "confirm-delete">("menu");
  const [name, setName] = useState(workspace.name);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setMode("menu");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const stop = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const submitRename = async (e: React.FormEvent) => {
    stop(e);
    if (!name.trim() || name === workspace.name) {
      setOpen(false);
      setMode("menu");
      return;
    }
    setBusy(true);
    try {
      await renameWorkspace(workspace.id, name.trim());
      setOpen(false);
      setMode("menu");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (e: React.MouseEvent) => {
    stop(e);
    setBusy(true);
    try {
      await deleteWorkspace(workspace.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
          setMode("menu");
          setName(workspace.name);
        }}
        className="grid h-7 w-7 place-items-center rounded text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
        aria-label="Workspace actions"
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-8 z-30 w-[220px] rounded-md border border-border bg-popover shadow-xl text-[12px]"
          onClick={stop}
        >
          {mode === "menu" && (
            <div className="py-1">
              <button
                onClick={(e) => {
                  stop(e);
                  setMode("rename");
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-foreground/90 hover:bg-surface-elevated"
              >
                <Pencil className="h-3.5 w-3.5" /> Rename
              </button>
              <button
                onClick={(e) => {
                  stop(e);
                  setMode("confirm-delete");
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-risk-high hover:bg-surface-elevated"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete workspace
              </button>
            </div>
          )}

          {mode === "rename" && (
            <form onSubmit={submitRename} className="p-3 space-y-2">
              <div className="label-eyebrow">New name</div>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    stop(e);
                    setMode("menu");
                  }}
                  className="px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-semibold disabled:opacity-60"
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          )}

          {mode === "confirm-delete" && (
            <div className="p-3 space-y-2">
              <div className="text-foreground/95">
                Delete <strong>{workspace.name}</strong>?
              </div>
              <div className="text-[11px] text-muted-foreground">
                Entities, relationships, and signals will be wiped from the graph too. This cannot be undone.
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={(e) => {
                    stop(e);
                    setMode("menu");
                  }}
                  className="px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={busy}
                  className="rounded bg-risk-high text-white px-2.5 py-1 text-[11px] font-semibold disabled:opacity-60"
                >
                  {busy ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
